import { Alert, Col, Divider, Input, Modal, Row, Skeleton, Space, Steps, Typography } from "antd";
import { AxiosError } from "axios";
import { BatchRange, BatchRangeConfirmation } from "models/cardIngestion";
import { FC, useState } from "react";

const { Title } = Typography;
const { Step } = Steps;

interface CustomStepProps {
    active: boolean
}

interface InputStepProps extends CustomStepProps {
    onBatchChange: (startBatch: string, endBatch: string) => void;
}

interface ConfirmStepProps extends CustomStepProps {
    confirm?: BatchRangeConfirmation;
}

interface ConfirmBatchesReviewedProps {
    open: boolean;
    onCancel: () => void;
    onCheck: (startBatch: string, endBatch: string) => Promise<BatchRangeConfirmation>;
    onConfirm: (startBatch: string, endBatch: string) => Promise<void>
}

const InputStep = ({ active, onBatchChange }: InputStepProps) => {
    const [startBatch, setStartBatch] = useState('');
    const [endBatch, setEndBatch] = useState('');

    if (!active) {
        return null;
    }

    return <Row gutter={[32, 32]}>
        <Col span={24}>
            <Space>
                Batches <Input
                    style={{ width: '75px' }}
                    value={startBatch}
                    onChange={e => {
                        setStartBatch(e.currentTarget.value);
                        onBatchChange(e.currentTarget.value, endBatch);
                    }}
                    placeholder="Start"
                />
                through
                <Input
                    style={{ width: '75px' }}
                    value={endBatch}
                    onChange={e => {
                        setEndBatch(e.currentTarget.value);
                        onBatchChange(startBatch, e.currentTarget.value);
                    }}
                    placeholder="End"
                />
                have passed QA.
            </Space>
        </Col>
        <Col span={24}>
            The 'Do Not Upload' flag on any cards within these batches will be respected.
        </Col>
    </Row>;
}

const ConfirmStep = ({ active, confirm }: ConfirmStepProps) => {
    if (!active) {
        return null;
    } else if (!confirm) {
        return <Skeleton />;
    }

    const { startBatch, endBatch, affectedCount } = confirm;

    return <Space direction="vertical" style={{ width: '100%', textAlign: 'center' }}>
        <Title> {`${startBatch} ➡ ${endBatch}`}</Title>

        <Title level={4}>You will be marking <div style={{ display: 'inline', color: 'var(--dcs-bright-blue)' }}>{affectedCount.toLocaleString()}</div> cards as reviewed. Are you sure?</Title>
    </Space>;
}

const ConfirmBatchesReviewedModal: FC<ConfirmBatchesReviewedProps> = ({
    open, onCancel, onCheck, onConfirm
}) => {
    const [working, setWorking] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [step, setStep] = useState(1);
    const [confirmResult, setConfirmResult] = useState<BatchRangeConfirmation | undefined>();
    const [range, setRange] = useState<BatchRange>({ startBatch: '', endBatch: '' });
    const [timerId, setTimerId] = useState<ReturnType<typeof setTimeout>>();
    const [onCooldown, setOnCooldown] = useState(false);

    const { startBatch, endBatch } = range;
    const valid = startBatch.trim() !== '' && endBatch.trim() !== '';

    const okText = step === 1
        ? 'Next'
        : onCooldown ? 'Wait...' : 'Yes';

    const cancelText = step === 1
        ? 'Cancel'
        : 'No!';

    const reset = () => {
        setRange({ startBatch: '', endBatch: '' });
        setError(null);
        setStep(1);
    }

    const startCooldown = (batchConf: BatchRangeConfirmation) => {
        setConfirmResult(batchConf);
        setOnCooldown(true);

        setStep(2);

        const timer = setTimeout(() => {
            cancelCooldown();
        }, 3000);
        setTimerId(timer);
    }

    const cancelCooldown = () => {
        clearInterval(timerId);
        setOnCooldown(false);
    }

    const check = async () => {
        try {
            setWorking(true);
            setError(null);

            const result = await onCheck(startBatch, endBatch);
            startCooldown(result);
        } catch (err) {
            if (err instanceof AxiosError) {
                setError(err.response?.data?.title ?? 'Unable to process bulk action');
            } else {
                setError('An unknown error occurred');
            }
        } finally {
            setWorking(false);
        }
    }

    const confirm = async () => {
        try {
            setWorking(true);
            setError(null);

            await onConfirm(startBatch, endBatch);
            reset();
        } catch (err) {
            if (err instanceof AxiosError) {
                setError(err.response?.data?.title ?? 'Unable to process bulk action');
            } else {
                setError('An unknown error occurred');
            }
        } finally {
            setWorking(false);
        }
    }

    return <Modal
        title="Mark Batches Ready for Upload"
        open={open}
        confirmLoading={working || onCooldown}
        cancelText={cancelText}
        onCancel={() => {
            if (step === 1) {
                reset();
                onCancel();
            } else {
                cancelCooldown();
                setStep(1);
            }
        }}
        onOk={async () => {
            if (step === 1) {
                await check();
            } else {
                await confirm();
            }
        }}
        okText={okText}
        okButtonProps={{ disabled: !valid }}
        closable={false}
        keyboard={false}
        maskClosable={false}
        destroyOnClose
    >
        <Steps
            size="small"
            current={step}
            direction="horizontal"
            responsive={false}
        >
            <Step title="Set Batch Range" />
            <Step title="Confirmation" />
        </Steps>

        <Divider />

        <InputStep
            active={step === 1}
            onBatchChange={(startBatch, endBatch) => {
                setRange({ startBatch, endBatch });
            }}
        />

        <ConfirmStep
            active={step === 2}
            confirm={confirmResult}
        />

        {error && <Alert
            className="bold-font"
            style={{ marginTop: '10px' }}
            message={error}
            type="error"
            showIcon
        />}
    </Modal>;
}

export default ConfirmBatchesReviewedModal;